import { useRef, useState } from 'react'
import { Avatar } from '../ui/Avatar'
import { confirmDialog } from '../ui/Dialog'
import { t, useI18n } from '../i18n'
import type { Member } from '../protocol'
import { Icon } from '../ui/Icon'
import { Popover } from '../ui/Popover'
import { displayNick, type RoomClient, type RoomState } from './client'

const RING = 2 * Math.PI * 21

/** The audience: one seat per member, showing who the room waits for. */
export function Seats({ client, state }: { client: RoomClient; state: RoomState }) {
  const { t } = useI18n()
  const roomWaiting = state.playback.status === 'waiting'
  const waited = new Set(roomWaiting ? (state.playback.waitingFor ?? []) : [])
  const members = [...state.members].sort((a, b) => Number(b.online) - Number(a.online))
  const isOwner = state.you?.role === 'owner'
  return (
    <section className="seats" aria-label={t('seats.label')}>
      <ul className="seats__row">
        {members.map((m) => (
          <Seat
            key={m.userId}
            member={m}
            you={m.userId === state.you?.userId}
            waited={waited.has(m.userId)}
            roomWaiting={roomWaiting}
            manage={isOwner && m.userId !== state.you?.userId ? client : null}
          />
        ))}
      </ul>
    </section>
  )
}

function seatStatus(m: Member, waited: boolean, roomWaiting: boolean) {
  if (!m.online) return t('seat.away')
  if (waited) return t('seat.waited', { pct: Math.round(Math.min(1, m.buffered / 4) * 100) })
  if (m.catchingUp) return t('seat.catchingUp')
  if (roomWaiting && m.state !== 'ready') return t('seat.loading')
  return t('seat.watching')
}

interface SeatProps {
  member: Member
  you: boolean
  waited: boolean
  roomWaiting: boolean
  manage: RoomClient | null
}

function Seat({ member, you, waited, roomWaiting, manage }: SeatProps) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const btn = useRef<HTMLButtonElement>(null)
  const progress = Math.min(1, member.buffered / 4)
  const cls = ['seat', member.online ? '' : 'seat--away', waited ? 'seat--waited' : '', you ? 'seat--you' : '']
    .filter(Boolean)
    .join(' ')
  const nick = displayNick(member.nick)

  const content = (
    <>
      <span className="seat__avatar">
        <svg viewBox="0 0 48 48" className="seat__ring" aria-hidden="true">
          <circle cx="24" cy="24" r="21" className="seat__ring-track" />
          {waited && (
            <circle
              cx="24"
              cy="24"
              r="21"
              className="seat__ring-fill"
              strokeDasharray={RING}
              strokeDashoffset={RING * (1 - progress)}
            />
          )}
        </svg>
        <Avatar src={member.avatar} name={nick} className="seat__picture" />
        {member.noWait && (
          <span className="seat__nowait" title={t('seat.noWaitBadge')}>
            <Icon name="no-wait" />
          </span>
        )}
        {member.role !== 'viewer' && (
          <span className="seat__badge" title={member.role === 'owner' ? t('seat.owner') : t('seat.controller')}>
            <Icon name={member.role === 'owner' ? 'crown' : 'star'} />
          </span>
        )}
      </span>
      {/* The full name on hover: long ones are cut. "You" goes with the
          status, where it is never cut off. */}
      <span className="seat__nick" title={nick}>
        {nick}
      </span>
      <span className="seat__status">
        {you ? t('seat.youStatus', { status: seatStatus(member, waited, roomWaiting) }) : seatStatus(member, waited, roomWaiting)}
      </span>
    </>
  )

  const act = (fn: () => void | Promise<void>) => () => {
    setOpen(false)
    void fn()
  }

  return (
    <li className={cls}>
      {manage ? (
        <button ref={btn} type="button" className="seat__button" onClick={() => setOpen(!open)} aria-expanded={open} aria-haspopup="menu">
          {content}
        </button>
      ) : (
        <div className="seat__button">{content}</div>
      )}
      {manage && (
        <Popover anchor={btn} open={open} onClose={() => setOpen(false)} side="top" align="center" label={nick}>
          <p className="popover__title">{nick}</p>
          {member.role === 'viewer' ? (
            <button type="button" onClick={act(() => manage.send({ t: 'set_role', userId: member.userId, role: 'controller' }))}>
              <span>{t('seat.allow')}</span>
              <span className="muted">{t('seat.allowHint')}</span>
            </button>
          ) : (
            <button type="button" onClick={act(() => manage.send({ t: 'set_role', userId: member.userId, role: 'viewer' }))}>
              <span>{t('seat.revoke')}</span>
            </button>
          )}
          <button
            type="button"
            onClick={act(async () => {
              if (await confirmDialog({ title: t('seat.makeOwnerConfirm', { nick }), confirmLabel: t('seat.makeOwner') })) {
                manage.send({ t: 'set_role', userId: member.userId, role: 'owner' })
              }
            })}
          >
            <span>{t('seat.makeOwner')}</span>
          </button>
          <button
            type="button"
            className={member.noWait ? 'is-active' : ''}
            role="menuitemcheckbox"
            aria-checked={!!member.noWait}
            onClick={act(() => manage.send({ t: 'set_no_wait', userId: member.userId, on: !member.noWait }))}
          >
            <span className="seat__check">
              {member.noWait && <Icon name="check" />}
              {t('seat.noWait')}
            </span>
            <span className="muted">{t('seat.noWaitHint')}</span>
          </button>
          <button
            type="button"
            className="danger"
            onClick={act(async () => {
              if (await confirmDialog({ title: t('seat.kickConfirm', { nick }), confirmLabel: t('seat.kick'), danger: true })) {
                manage.send({ t: 'kick', userId: member.userId })
              }
            })}
          >
            <span>{t('seat.kick')}</span>
          </button>
        </Popover>
      )}
    </li>
  )
}
